import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/core/hooks/useAuth";
import { useMinisteriosQueGerencio, useMinisteriosQueLidero } from "@/core/hooks/useMinisterios";
import { acessoService } from "@/core/services/acessoService";
import { PAPEL_MEMBRO, type PapelAjuda } from "@/core/ajuda/licoes";

/** Elos que têm gestão própria, na ordem do menu. Escala não entra: é por ministério. */
const ELOS_COM_GESTAO = ["biblioteca", "eventos", "reservas", "comunicados", "visitantes"];

/**
 * O papel da pessoa para a Ajuda e para os cartões do Início: lidera um ministério? é Gestor de
 * elo? quais elos gerencia? Usa as mesmas fontes que o resto do Elos (a regra `gerencia_modulo`
 * do banco, os vínculos da própria pessoa). Só ORIENTA: não concede nem amplia nada, e qualquer
 * falha cai em "membro comum", que é o lado seguro.
 *
 * `pronto` fica falso enquanto o papel carrega: quem abre uma lição pelo endereço (/ajuda/gestor)
 * espera a resposta em vez de ser mandado para a lista.
 */
export function useAjudaPapel(): { papel: PapelAjuda; pronto: boolean } {
  const {
    pessoa,
    ehAdminPlataforma,
    modulosVisiveis,
    podeGerenciarBiblioteca,
    podeGerenciarEventos,
    podeGerenciarReservas,
  } = useAuth();
  const lideranca = useMinisteriosQueLidero();
  const gestao = useMinisteriosQueGerencio();

  // Biblioteca, Eventos e Reservas já vêm do AuthContext. Comunicados e Visitantes não têm
  // sinalizador pronto: pergunta ao banco, só se o elo estiver liberado para a pessoa.
  const consultar = ["comunicados", "visitantes"].filter((c) => modulosVisiveis.includes(c));
  const extras = useQuery({
    queryKey: ["ajuda-elos-gestao", pessoa?.id ?? "", consultar.join(",")],
    queryFn: async () => {
      const respostas = await Promise.all(consultar.map((c) => acessoService.podeGerenciarModulo(c)));
      return consultar.filter((_, i) => respostas[i]);
    },
    enabled: !!pessoa,
  });

  const papel = useMemo<PapelAjuda>(() => {
    if (!pessoa) return PAPEL_MEMBRO;
    const gerenciados = new Set<string>(extras.data ?? []);
    if (podeGerenciarBiblioteca) gerenciados.add("biblioteca");
    if (podeGerenciarEventos) gerenciados.add("eventos");
    if (podeGerenciarReservas) gerenciados.add("reservas");
    return {
      // Como no Início: o administrador da plataforma não recebe os cartões nem as lições de
      // liderança. Os elos que ele gerencia por um vínculo continuam valendo.
      lider: !ehAdminPlataforma && (lideranca.data?.length ?? 0) > 0,
      gestor: !ehAdminPlataforma && (gestao.data?.length ?? 0) > 0,
      elos: ELOS_COM_GESTAO.filter((c) => gerenciados.has(c) && modulosVisiveis.includes(c)),
    };
  }, [
    pessoa,
    ehAdminPlataforma,
    lideranca.data,
    gestao.data,
    extras.data,
    modulosVisiveis,
    podeGerenciarBiblioteca,
    podeGerenciarEventos,
    podeGerenciarReservas,
  ]);

  const pronto = !!pessoa && !lideranca.isPending && !gestao.isPending && !extras.isPending;
  return { papel, pronto };
}
